import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiArrowLeft, FiDownload, FiFilter, FiPackage, FiFileText, FiSearch } from 'react-icons/fi';
import { reportesService } from '../../services';
import { exportToCsv, exportToPdf } from '../../utils/exportReport';
import './ReportesCommon.scss';

const ReportesInventario = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState(null);

  // Filtros
  const [department, setDepartment] = useState('all'); // all, betel_natural, naturalandlab, otras_marcas, ingredientes, materia_prima
  const [lowStockOnly, setLowStockOnly] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchInput, setSearchInput] = useState('');

  // Paginación
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10); // Cambiado a 10 para testing
  const [pagination, setPagination] = useState(null);

  useEffect(() => {
    loadReportData();
  }, [department, lowStockOnly, searchQuery, currentPage, pageSize]);

  const loadReportData = async () => {
    setLoading(true);
    try {
      const params = {
        department: department !== 'all' ? department : undefined,
        low_stock_only: lowStockOnly || undefined,
        search: searchQuery || undefined,
        page: currentPage,
        page_size: pageSize
      };

      const response = await reportesService.getInventarioDetallado(params);

      if (response.success) {
        setReportData(response.data);
        setPagination(response.data.pagination);
        console.log('Pagination data:', response.data.pagination); // Debug
      }
    } catch (error) {
      console.error('Error al cargar reporte de inventario:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setSearchQuery(searchInput);
    setCurrentPage(1); // Reset a la primera página cuando se busca
  };

  const handleFilterChange = (filterFn) => {
    filterFn();
    setCurrentPage(1); // Reset a la primera página cuando cambian filtros
  };

  // La tabla está paginada en el servidor (10 filas por página): para
  // exportar hay que pedir el listado COMPLETO con los filtros activos,
  // no el trozo visible (antes salía solo la página en pantalla).
  const fetchAllItems = async () => {
    const baseParams = {
      department: department !== 'all' ? department : undefined,
      low_stock_only: lowStockOnly || undefined,
      search: searchQuery || undefined
    };
    // El backend limita page_size a 500: se recorren las páginas necesarias.
    const all = [];
    let page = 1;
    for (;;) {
      const response = await reportesService.getInventarioDetallado({
        ...baseParams,
        page,
        page_size: 500
      });
      if (!response.success) {
        alert('No se pudo obtener el inventario completo para exportar.');
        return null;
      }
      const items = response.data.items || [];
      all.push(...items);
      if (!response.data.pagination?.has_next || items.length === 0) break;
      page += 1;
    }
    return all;
  };

  const exportColumns = [
    { label: 'Producto', value: (r) => r.product_name },
    { label: 'Departamento', value: (r) => departmentOptions.find(d => d.value === r.department)?.label || r.department },
    { label: 'Stock Actual', value: (r) => r.current_stock },
    { label: 'Stock Mínimo', value: (r) => r.min_stock },
    { label: 'Estado', value: (r) => getStockStatusLabel(getStockStatus(r.current_stock, r.min_stock)) },
    { label: 'Costo Unitario', value: (r) => formatCurrency(r.unit_cost) },
    { label: 'Valor Total', value: (r) => formatCurrency(r.total_value) },
    { label: 'Último Movimiento', value: (r) => r.last_movement_date || 'N/A' }
  ];

  const handleExportPDF = async () => {
    const items = await fetchAllItems();
    if (!items) return;
    const departmentLabel = departmentOptions.find(d => d.value === department)?.label || 'Todos';
    exportToPdf(
      `Reporte de Inventario - ${departmentLabel}`,
      exportColumns,
      items,
      `${items.length} productos · ${new Date().toLocaleDateString('es-MX')}`
    );
  };

  const handleExportCSV = async () => {
    const items = await fetchAllItems();
    if (!items) return;
    if (items.length === 0) {
      alert('No hay datos para exportar');
      return;
    }
    const filename = `inventario_${department}_${new Date().toISOString().split('T')[0]}.csv`;
    exportToCsv(exportColumns, items, filename);
  };

  const handleExportExcel = async () => {
    const items = await fetchAllItems();
    if (!items) return;
    if (items.length === 0) {
      alert('No hay datos para exportar');
      return;
    }

    const esc = (s) => String(s ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
    const thead = exportColumns.map((c) => `<th>${esc(c.label)}</th>`).join('');
    const tbody = items
      .map((r) => `<tr>${exportColumns.map((c) => `<td>${esc(c.value(r))}</td>`).join('')}</tr>`)
      .join('');
    const html = `
      <html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel" xmlns="http://www.w3.org/TR/REC-html40">
        <head><meta charset="utf-8"></head>
        <body><table border="1"><thead><tr>${thead}</tr></thead><tbody>${tbody}</tbody></table></body>
      </html>
    `;
    const blob = new Blob(['﻿' + html], { type: 'application/vnd.ms-excel' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `inventario_${department}_${new Date().toISOString().split('T')[0]}.xls`;
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const departmentOptions = [
    { value: 'all', label: 'Todos los Departamentos' },
    { value: 'betel_natural', label: 'Betel Natural' },
    { value: 'naturalandlab', label: 'Naturalandlab' },
    { value: 'otras_marcas', label: 'Otras Marcas' },
    { value: 'ingredientes', label: 'Ingredientes' },
    { value: 'materia_prima', label: 'Materia Prima' }
  ];

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(value || 0);
  };

  const getStockStatus = (currentStock, minStock) => {
    if (currentStock === 0) return 'sin-stock';
    if (currentStock <= minStock) return 'bajo-stock';
    return 'normal';
  };

  const getStockStatusLabel = (status) => {
    const labels = {
      'sin-stock': 'Sin Stock',
      'bajo-stock': 'Bajo Stock',
      'normal': 'Normal'
    };
    return labels[status] || 'Normal';
  };

  // Los items ya vienen paginados del backend
  const displayItems = reportData?.items || [];

  return (
    <div className="reportes-detail-page">
      {/* Header */}
      <div className="reportes-detail-header">
        <div className="header-top">
          <button className="btn-back" onClick={() => navigate('/system/reportes')}>
            <FiArrowLeft />
            <span>Regresar a Reportes</span>
          </button>
          <h1 className="reportes-detail-title">Reporte de Inventario</h1>
        </div>

        <div className="header-actions">
          <button className="btn-export" onClick={handleExportPDF} title="Exportar a PDF">
            <FiFileText />
            <span>PDF</span>
          </button>
          <button className="btn-export btn-export--secondary" onClick={handleExportCSV} title="Exportar a CSV">
            <FiDownload />
            <span>CSV</span>
          </button>
          <button className="btn-export btn-export--secondary" onClick={handleExportExcel} title="Exportar a Excel">
            <FiDownload />
            <span>Excel</span>
          </button>
        </div>
      </div>

      {/* Filtros */}
      <div className="reportes-filters">
        <div className="filter-group">
          <label className="filter-label">
            <FiSearch />
            <span>Buscar Producto</span>
          </label>
          <form onSubmit={handleSearchSubmit} style={{ display: 'flex', gap: '8px' }}>
            <input
              type="text"
              className="filter-input"
              placeholder="Buscar por nombre de producto..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
            />
            <button type="submit" className="btn-search">
              Buscar
            </button>
          </form>
        </div>

        <div className="filter-group">
          <label className="filter-label">
            <FiFilter />
            <span>Departamento</span>
          </label>
          <select
            className="filter-select"
            value={department}
            onChange={(e) => handleFilterChange(() => setDepartment(e.target.value))}
          >
            {departmentOptions.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>

        <div className="filter-group">
          <label className="filter-label">
            <FiPackage />
            <span>Filtrar</span>
          </label>
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={lowStockOnly}
              onChange={(e) => handleFilterChange(() => setLowStockOnly(e.target.checked))}
              className="filter-checkbox"
            />
            <span>Solo productos con bajo stock</span>
          </label>
        </div>
      </div>

      {/* Resumen */}
      {reportData?.summary && (
        <div className="reportes-summary">
          <div className="summary-card">
            <span className="summary-label">Total Productos</span>
            <span className="summary-value">{reportData.summary.total_products}</span>
          </div>
          <div className="summary-card">
            <span className="summary-label">En Stock</span>
            <span className="summary-value success">{reportData.summary.in_stock}</span>
          </div>
          <div className="summary-card">
            <span className="summary-label">Bajo Stock</span>
            <span className="summary-value" style={{ color: '#f59e0b' }}>{reportData.summary.low_stock}</span>
          </div>
          <div className="summary-card">
            <span className="summary-label">Sin Stock</span>
            <span className="summary-value discount">{reportData.summary.out_of_stock}</span>
          </div>
          <div className="summary-card highlight">
            <span className="summary-label">Valor Total</span>
            <span className="summary-value">{formatCurrency(reportData.summary.total_value)}</span>
          </div>
        </div>
      )}

      {/* Tabla de Inventario */}
      <div className="reportes-table-container">
        {loading ? (
          <div className="loading-state">Cargando reporte...</div>
        ) : displayItems.length > 0 ? (
          <>
            <table className="reportes-table">
              <thead>
                <tr>
                  <th>Producto</th>
                  <th>Departamento</th>
                  <th className="text-right">Stock Actual</th>
                  <th className="text-right">Stock Mínimo</th>
                  <th className="text-center">Estado</th>
                  <th className="text-right">Costo Unitario</th>
                  <th className="text-right">Valor Total</th>
                  <th className="text-right">Último Movimiento</th>
                </tr>
              </thead>
              <tbody>
                {displayItems.map((item) => {
                const status = getStockStatus(item.current_stock, item.min_stock);
                return (
                  <tr key={item.product_id}>
                    <td className="font-bold">{item.product_name}</td>
                    <td>
                      <span className="department-badge">
                        {departmentOptions.find(d => d.value === item.department)?.label || item.department}
                      </span>
                    </td>
                    <td className="text-right font-bold">{item.current_stock}</td>
                    <td className="text-right">{item.min_stock}</td>
                    <td className="text-center">
                      <span className={`stock-status stock-status--${status}`}>
                        {getStockStatusLabel(status)}
                      </span>
                    </td>
                    <td className="text-right">{formatCurrency(item.unit_cost)}</td>
                    <td className="text-right font-bold">{formatCurrency(item.total_value)}</td>
                    <td className="text-right">{item.last_movement_date || 'N/A'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {/* Paginador */}
          {pagination && (
            <div className="pagination-container">
              <div className="pagination-info">
                Mostrando {((pagination.page - 1) * pagination.page_size) + 1} - {Math.min(pagination.page * pagination.page_size, pagination.total_items)} de {pagination.total_items} productos
              </div>
              {pagination.total_pages > 1 && (
                <div className="pagination-controls">
                  <button
                    className="pagination-btn"
                    onClick={() => setCurrentPage(1)}
                    disabled={!pagination.has_previous}
                  >
                    Primera
                  </button>
                  <button
                    className="pagination-btn"
                    onClick={() => setCurrentPage(currentPage - 1)}
                    disabled={!pagination.has_previous}
                  >
                    Anterior
                  </button>
                  <span className="pagination-current">
                    Página {pagination.page} de {pagination.total_pages}
                  </span>
                  <button
                    className="pagination-btn"
                    onClick={() => setCurrentPage(currentPage + 1)}
                    disabled={!pagination.has_next}
                  >
                    Siguiente
                  </button>
                  <button
                    className="pagination-btn"
                    onClick={() => setCurrentPage(pagination.total_pages)}
                    disabled={!pagination.has_next}
                  >
                    Última
                  </button>
                </div>
              )}
            </div>
          )}
          </>
        ) : reportData?.items && reportData.items.length > 0 ? (
          <div className="empty-state">
            <p>No se encontraron productos que coincidan con "{searchQuery}".</p>
          </div>
        ) : (
          <div className="empty-state">
            <p>No hay productos en inventario para mostrar.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ReportesInventario;
